import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator, NamingConvention } from "namirasoft-core";
import { DatabaseRow } from "namirasoft-credential";
import { EnvironmentRow, KubernetesRow, MonitorCallerType, MonitorDockerContainerConnectionType, MonitorHealthCheckRetentionUnit, MonitorHeartbeatRetryIntervalUnit, MonitorHTTPBodyEncoding, MonitorInputRow, MonitorKubernetesPodControllerType, MonitorRequestTimeoutUnit, MonitorRow, MonitorType, ProjectRow, ServerRow } from "namirasoft-infra";
import { LogGroupRow } from "namirasoft-log";
import { TopicRow } from "namirasoft-notification-sender";
import { SchedulerRow } from "namirasoft-scheduler";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBox, NSBoxBoolean, NSBoxEntity, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSBoxURL, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InfraEntities } from "../InfraEntities";
import { InfraLayout } from '../InfraLayout';
import { InfraRouterProps } from "../InfraRouterProps";

interface MonitorEditPageProps extends NSARouterMakerResult, InfraRouterProps
{
    isEdit: boolean;
}

interface MonitorEditPageState
{
    selected_project_id?: string;
    selected_environment_id?: string;
    monitor_type?: MonitorType;
    caller_type?: MonitorCallerType;
}

export default class MonitorEditPage extends Component<MonitorEditPageProps, MonitorEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEntity_ProjectID = createRef<NSBoxEntity<ProjectRow>>();
    NSBoxEntity_EnvironmentID = createRef<NSBoxEntity<EnvironmentRow>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxEntity_TopicID = createRef<NSBoxEntity<TopicRow>>();
    NSBoxEnum_CallerType = createRef<NSBoxEnum<MonitorCallerType>>();
    NSBoxEntity_CallerServerID = createRef<NSBoxEntity<ServerRow>>();
    NSBoxEntity_CallerKubernetesID = createRef<NSBoxEntity<KubernetesRow>>();
    NSBoxBoolean_UpSideDown = createRef<NSBoxBoolean>();
    NSBoxEntity_HeartbeatIntervalSchedulerID = createRef<NSBoxEntity<SchedulerRow>>();
    NSBoxInteger_Retries = createRef<NSBoxInteger>();
    NSBoxInteger_HeartbeatRetryIntervalValue = createRef<NSBoxInteger>();
    NSBoxInteger_RequestTimeoutValue = createRef<NSBoxInteger>();
    NSBoxInteger_HealthCheckRetentionValue = createRef<NSBoxInteger>();
    NSBoxEnum_Type = createRef<NSBoxEnum<MonitorType>>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    NSBoxURL_HTTP_URL = createRef<NSBoxURL>();
    NSBoxString_HTTP_Method = createRef<NSBoxString>();
    NSBoxString_HTTP_Header = createRef<NSBoxString>();
    NSBoxEnum_HTTP_BodyEncoding = createRef<NSBoxEnum<MonitorHTTPBodyEncoding>>();
    NSBoxString_HTTP_Body = createRef<NSBoxString>();
    NSBoxBoolean_HTTP_IgnoreSslTls = createRef<NSBoxBoolean>();

    NSBoxString_Ping_Host = createRef<NSBoxString>();
    NSBoxString_Ping_MaxPackets = createRef<NSBoxString>();
    NSBoxString_Ping_PacketSize = createRef<NSBoxString>();

    NSBoxString_Port_Host = createRef<NSBoxString>();
    NSBoxInteger_Port_Port = createRef<NSBoxInteger>();

    NSBoxEntity_Database_CredentialID = createRef<NSBoxEntity<DatabaseRow>>();
    NSBoxTextArea_Database_Query = createRef<NSBoxTextArea>();

    NSBoxEntity_Cache_CredentialID = createRef<NSBoxEntity<DatabaseRow>>();

    NSBoxEntity_Messaging_CredentialID = createRef<NSBoxEntity<DatabaseRow>>();

    NSBoxEnum_DockerContainer_ConnectionType = createRef<NSBoxEnum<MonitorDockerContainerConnectionType>>();
    NSBoxString_DockerContainer_Deamon = createRef<NSBoxString>();
    NSBoxString_DockerContainer_IdOrName = createRef<NSBoxString>();

    NSBoxEnum_KubernetesPod_ControllerType = createRef<NSBoxEnum<MonitorKubernetesPodControllerType>>();
    NSBoxString_KubernetesPod_ControllerName = createRef<NSBoxString>();

    constructor(props: MonitorEditPageProps)
    {
        super(props);
        this.state = {};
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <InfraLayout<MonitorRow, MonitorInputRow>
                {...this.props}
                pages={{
                    entity: InfraEntities.monitor(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): MonitorInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let project_id = this.NSBoxEntity_ProjectID.current?.getValueOne() ?? "";
                                        let environment_id = this.NSBoxEntity_EnvironmentID.current?.getValueOne() ?? "";
                                        let log_group_id = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? null;
                                        let topic_id = this.NSBoxEntity_TopicID.current?.getValueOne() ?? "";
                                        let caller_type = this.NSBoxEnum_CallerType.current?.getValueEnumOne(MonitorCallerType, MonitorCallerType.Globally) ?? MonitorCallerType.Globally;
                                        let caller_server_id = this.NSBoxEntity_CallerServerID.current?.getValueOne(this.state.caller_type === MonitorCallerType.ByServer) ?? null;
                                        let caller_kubernetes_id = this.NSBoxEntity_CallerKubernetesID.current?.getValueOne(this.state.caller_type === MonitorCallerType.ByKubernetes) ?? null;
                                        let up_side_down = this.NSBoxBoolean_UpSideDown.current?.getValueBool() ?? false;
                                        let heartbeat_interval_scheduler_id = this.NSBoxEntity_HeartbeatIntervalSchedulerID.current?.getValueOne() ?? "";
                                        let retries = this.NSBoxInteger_Retries.current?.getValue() ?? 0;
                                        let heartbeat_retry_interval_value = this.NSBoxInteger_HeartbeatRetryIntervalValue.current?.getValue() ?? 0;
                                        let heartbeat_retry_interval_unit = this.NSBoxInteger_HeartbeatRetryIntervalValue.current?.NSBoxCombo_Ref.current?.getValueOne() as MonitorHeartbeatRetryIntervalUnit;
                                        let request_timeout_value = this.NSBoxInteger_RequestTimeoutValue.current?.getValue() ?? 0;
                                        let request_timeout_unit = this.NSBoxInteger_RequestTimeoutValue.current?.NSBoxCombo_Ref.current?.getValueOne() as MonitorRequestTimeoutUnit;
                                        let health_check_retention_value = this.NSBoxInteger_HealthCheckRetentionValue.current?.getValue() ?? 0;
                                        let health_check_retention_unit = this.NSBoxInteger_HealthCheckRetentionValue.current?.NSBoxCombo_Ref.current?.getValueOne() as MonitorHealthCheckRetentionUnit;
                                        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(MonitorType, MonitorType.Http) ?? MonitorType.Http;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;

                                        let http_url = this.NSBoxURL_HTTP_URL.current?.getValue() ?? null;
                                        let http_method = this.NSBoxString_HTTP_Method.current?.getValue() ?? null;
                                        let http_header = this.NSBoxString_HTTP_Header.current?.getValue() ?? null;
                                        let http_body_encoding = this.NSBoxEnum_HTTP_BodyEncoding.current?.getValueEnumOne(MonitorHTTPBodyEncoding, MonitorHTTPBodyEncoding.Json) ?? null;
                                        let http_body = this.NSBoxString_HTTP_Body.current?.getValue() ?? null;
                                        let http_ignore_ssl_tls = this.NSBoxBoolean_HTTP_IgnoreSslTls.current?.getValue() ?? null;

                                        let ping_host = this.NSBoxString_Ping_Host.current?.getValue() ?? null;
                                        let ping_max_packets = this.NSBoxString_Ping_MaxPackets.current?.getValue() ?? null;
                                        let ping_packet_size = this.NSBoxString_Ping_PacketSize.current?.getValue() ?? null;

                                        let port_host = this.NSBoxString_Port_Host.current?.getValue() ?? null;
                                        let port_port = this.NSBoxInteger_Port_Port.current?.getValue() ?? null;

                                        let database_credentail_id = this.NSBoxEntity_Database_CredentialID.current?.getValueOne() ?? null;
                                        let database_query = this.NSBoxTextArea_Database_Query.current?.getValue() ?? null;

                                        let cache_credentail_id = this.NSBoxEntity_Cache_CredentialID.current?.getValueOne() ?? null;

                                        let messaging_credentail_id = this.NSBoxEntity_Messaging_CredentialID.current?.getValueOne() ?? null;

                                        let docker_container_connection_type = this.NSBoxEnum_DockerContainer_ConnectionType.current?.getValueEnumOne(MonitorDockerContainerConnectionType, MonitorDockerContainerConnectionType.Socket) ?? null;
                                        let docker_container_deamon = this.NSBoxString_DockerContainer_Deamon.current?.getValue() ?? null;
                                        let docker_container_id_or_name = this.NSBoxString_DockerContainer_IdOrName.current?.getValue() ?? null;

                                        let kubernetes_pod_controller_type = this.NSBoxEnum_KubernetesPod_ControllerType.current?.getValueEnumOne(MonitorKubernetesPodControllerType, MonitorKubernetesPodControllerType.Deamonset) ?? null;
                                        let kubernetes_pod_controller_name = this.NSBoxString_KubernetesPod_ControllerName.current?.getValue() ?? "";

                                        return {
                                            workspace_id: "",
                                            project_id,
                                            environment_id,
                                            log_group_id,
                                            topic_id,
                                            name,
                                            caller_type,
                                            caller_server_id,
                                            caller_kubernetes_id,
                                            up_side_down,
                                            heartbeat_interval_scheduler_id,
                                            retries,
                                            heartbeat_retry_interval_value,
                                            heartbeat_retry_interval_unit,
                                            request_timeout_value,
                                            request_timeout_unit,
                                            health_check_retention_value,
                                            health_check_retention_unit,
                                            type,
                                            description,

                                            http_url,
                                            http_method,
                                            http_header,
                                            http_body_encoding,
                                            http_body,
                                            http_ignore_ssl_tls,

                                            ping_host,
                                            ping_max_packets,
                                            ping_packet_size,

                                            port_host,
                                            port_port,

                                            database_credentail_id,
                                            database_query,

                                            cache_credentail_id,

                                            messaging_credentail_id,

                                            docker_container_connection_type,
                                            docker_container_deamon,
                                            docker_container_id_or_name,

                                            kubernetes_pod_controller_type,
                                            kubernetes_pod_controller_name,

                                            monitor_category: [],
                                            monitor_field: [],
                                            monitor_tag: [],
                                        };
                                    },
                                    setRow: (row: MonitorRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEntity_ProjectID.current?.setValueOne(row.project_id);
                                        this.NSBoxEntity_LogGroupID.current?.setValueOne(row.log_group_id);
                                        this.NSBoxEntity_TopicID.current?.setValueOne(row.topic_id);
                                        this.NSBoxEnum_CallerType.current?.setValueOne(row.caller_type);
                                        this.NSBoxBoolean_UpSideDown.current?.setValueBool(row.up_side_down);
                                        this.NSBoxEntity_HeartbeatIntervalSchedulerID.current?.setValueOne(row.heartbeat_interval_scheduler_id);
                                        this.NSBoxInteger_Retries.current?.setValue(row.retries);
                                        this.NSBoxInteger_HeartbeatRetryIntervalValue.current?.setValue(row.heartbeat_retry_interval_value);
                                        this.NSBoxInteger_HeartbeatRetryIntervalValue.current?.NSBoxCombo_Ref.current?.setValueOne(row.heartbeat_retry_interval_unit);
                                        this.NSBoxInteger_RequestTimeoutValue.current?.setValue(row.request_timeout_value);
                                        this.NSBoxInteger_RequestTimeoutValue.current?.NSBoxCombo_Ref.current?.setValueOne(row.request_timeout_unit);
                                        this.NSBoxInteger_HealthCheckRetentionValue.current?.setValue(row.health_check_retention_value);
                                        this.NSBoxInteger_HealthCheckRetentionValue.current?.NSBoxCombo_Ref.current?.setValueOne(row.health_check_retention_unit);
                                        this.NSBoxEnum_Type.current?.setValueOne(row.type);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description ?? "");

                                        this.setState(
                                            () => ({
                                                selected_project_id: row.project_id,
                                                selected_environment_id: row.environment_id,
                                                monitor_type: row.type,
                                                caller_type: row.caller_type
                                            }),
                                            () =>
                                            {
                                                this.NSBoxEntity_EnvironmentID.current?.setValueOne(row.environment_id);
                                                this.NSBoxEntity_CallerServerID.current?.setValueOne(row.caller_server_id);
                                                this.NSBoxEntity_CallerKubernetesID.current?.setValueOne(row.caller_kubernetes_id);

                                                this.NSBoxURL_HTTP_URL.current?.setValue(row.http_url);
                                                this.NSBoxString_HTTP_Method.current?.setValue(row.http_method);
                                                this.NSBoxString_HTTP_Header.current?.setValue(row.http_header);
                                                this.NSBoxEnum_HTTP_BodyEncoding.current?.setValueOne(row.http_body_encoding);
                                                this.NSBoxString_HTTP_Body.current?.setValue(row.http_body);
                                                this.NSBoxBoolean_HTTP_IgnoreSslTls.current?.setValue(row.http_ignore_ssl_tls);

                                                this.NSBoxString_Ping_Host.current?.setValue(row.ping_host);
                                                this.NSBoxString_Ping_MaxPackets.current?.setValue(row.ping_max_packets);
                                                this.NSBoxString_Ping_PacketSize.current?.setValue(row.ping_packet_size);

                                                this.NSBoxString_Port_Host.current?.setValue(row.port_host);
                                                this.NSBoxInteger_Port_Port.current?.setValue(row.port_port);

                                                this.NSBoxEntity_Database_CredentialID.current?.setValueOne(row.database_credentail_id);
                                                this.NSBoxTextArea_Database_Query.current?.setValue(row.database_query);

                                                this.NSBoxEntity_Cache_CredentialID.current?.setValueOne(row.cache_credentail_id);

                                                this.NSBoxEntity_Messaging_CredentialID.current?.setValueOne(row.messaging_credentail_id);

                                                this.NSBoxEnum_DockerContainer_ConnectionType.current?.setValueOne(row.docker_container_connection_type);
                                                this.NSBoxString_DockerContainer_Deamon.current?.setValue(row.docker_container_deamon);
                                                this.NSBoxString_DockerContainer_IdOrName.current?.setValue(row.docker_container_id_or_name);

                                                this.NSBoxEnum_KubernetesPod_ControllerType.current?.setValueOne(row.kubernetes_pod_controller_type);
                                                this.NSBoxString_KubernetesPod_ControllerName.current?.setValue(row.kubernetes_pod_controller_name);
                                            }
                                        );
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Caller",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEnum<MonitorCallerType>
                                                        ref={this.NSBoxEnum_CallerType}
                                                        title="Type"
                                                        required
                                                        multiple={false}
                                                        getEnumObject={() => MonitorCallerType}
                                                        getTitle={(item) => NamingConvention.auto.convert(item, NamingConvention.Pascal_Case_Space)}
                                                        onChanged={(box) => this.setState(() => ({ caller_type: box.getValueOne() as MonitorCallerType }))}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {
                                                        this.state.caller_type === MonitorCallerType.ByServer &&
                                                        <NSBoxEntity<ServerRow>
                                                            ref={this.NSBoxEntity_CallerServerID}
                                                            title="Server"
                                                            required
                                                            multiple={false}
                                                            table_name="server"
                                                            list={async (filters, page, size, sorts) =>
                                                            {
                                                                if (!filters)
                                                                    filters = [];

                                                                if (this.state.selected_environment_id)
                                                                    filters.push(new FilterItem(
                                                                        namirasoft.infra.tables.server,
                                                                        namirasoft.infra.tables.server.columns.environment_id,
                                                                        false,
                                                                        FilterItemOperator.all.equals,
                                                                        this.state.selected_environment_id,
                                                                    ));

                                                                return await this.props.servers.infra.server.List(filters, page, size, sorts, null);
                                                            }}
                                                            getValue={(item) => item.id}
                                                            getTitle={(item) => item.name}
                                                            getSort={() => null}
                                                            style={{ width: NSBox.width.double }}
                                                        />
                                                    }
                                                    {
                                                        this.state.caller_type === MonitorCallerType.ByKubernetes &&
                                                        <NSBoxEntity<KubernetesRow>
                                                            ref={this.NSBoxEntity_CallerKubernetesID}
                                                            title="Kubernetes"
                                                            required
                                                            multiple={false}
                                                            table_name="kubernetes"
                                                            list={async (filters, page, size, sort, all_workspaces) =>
                                                            {
                                                                if (!filters)
                                                                    filters = [];

                                                                filters.push(new FilterItem(
                                                                    namirasoft.infra.tables.kubernetes,
                                                                    namirasoft.infra.tables.kubernetes.columns.agent_pod_installation_enabled,
                                                                    false,
                                                                    FilterItemOperator.all.equals,
                                                                    "1",
                                                                ));

                                                                if (this.state.selected_environment_id)
                                                                    filters.push(new FilterItem(
                                                                        namirasoft.infra.tables.kubernetes,
                                                                        namirasoft.infra.tables.kubernetes.columns.environment_id,
                                                                        false,
                                                                        FilterItemOperator.all.equals,
                                                                        this.state.selected_environment_id,
                                                                    ));

                                                                return await this.props.servers.infra.kubernetes.List(filters, page, size, sort, all_workspaces);
                                                            }}
                                                            getValue={(item) => item.id}
                                                            getTitle={(item) => item.name}
                                                            getSort={() => null}
                                                            style={{ width: NSBox.width.double }}
                                                        />
                                                    }
                                                </NSColumn>
                                            )
                                        },
                                        {
                                            title: "Heartbeat",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEntity<SchedulerRow>
                                                        ref={this.NSBoxEntity_HeartbeatIntervalSchedulerID}
                                                        required
                                                        title="Heartbeat Interval Scheduler"
                                                        multiple={false}
                                                        table_name="scheduler"
                                                        table={namirasoft.scheduler.tables.scheduler}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => null}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSRow style={{ justifyContent: "flex-start" }}>
                                                        <NSBoxInteger
                                                            ref={this.NSBoxInteger_Retries}
                                                            required
                                                            title="Retries"
                                                        />
                                                        <NSBoxInteger
                                                            ref={this.NSBoxInteger_HeartbeatRetryIntervalValue}
                                                            required
                                                            title="Heartbeat Retry Interval"
                                                            combo={{
                                                                multiple: false,
                                                                placeholder: "Unit",
                                                                getOptions: () => Object.values(MonitorHeartbeatRetryIntervalUnit).map(item => ({ title: item, value: item })),
                                                            }}
                                                        />
                                                    </NSRow>
                                                    <NSRow>
                                                        <NSBoxInteger
                                                            ref={this.NSBoxInteger_RequestTimeoutValue}
                                                            required
                                                            title="Request Timeout"
                                                            combo={{
                                                                multiple: false,
                                                                placeholder: "Unit",
                                                                getOptions: () => Object.values(MonitorRequestTimeoutUnit).map(item => ({ title: item, value: item })),
                                                            }}
                                                        />
                                                        <NSBoxInteger
                                                            ref={this.NSBoxInteger_HealthCheckRetentionValue}
                                                            required
                                                            title="Health Check Retention"
                                                            combo={{
                                                                multiple: false,
                                                                placeholder: "Unit",
                                                                getOptions: () => Object.values(MonitorHealthCheckRetentionUnit).map(item => ({ title: item, value: item })),
                                                            }}
                                                        />
                                                    </NSRow>
                                                </NSColumn>
                                            )
                                        },
                                        {
                                            title: "Monitor Type",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEnum<MonitorType>
                                                        ref={this.NSBoxEnum_Type}
                                                        title="Monitor Type"
                                                        required
                                                        multiple={false}
                                                        getEnumObject={() => MonitorType}
                                                        getTitle={(item) => NamingConvention.auto.convert(item, NamingConvention.Pascal_Case_Space)}
                                                        onChanged={(box) => this.setState(() => ({ monitor_type: box.getValueOne() as MonitorType }))}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {
                                                        this.state.monitor_type === MonitorType.Http &&
                                                        <>
                                                            <NSBoxURL
                                                                ref={this.NSBoxURL_HTTP_URL}
                                                                required={false}
                                                                title="URL"
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSRow>
                                                                <NSBoxString
                                                                    ref={this.NSBoxString_HTTP_Method}
                                                                    title="Method"
                                                                    required={false}
                                                                />
                                                                <NSBoxEnum<MonitorHTTPBodyEncoding>
                                                                    ref={this.NSBoxEnum_HTTP_BodyEncoding}
                                                                    title="Body Encoding"
                                                                    required={false}
                                                                    multiple={false}
                                                                    getEnumObject={() => MonitorHTTPBodyEncoding}
                                                                />
                                                            </NSRow>
                                                            <NSBoxString
                                                                ref={this.NSBoxString_HTTP_Header}
                                                                required={false}
                                                                title="Header"
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxString
                                                                ref={this.NSBoxString_HTTP_Body}
                                                                required={false}
                                                                title="Body"
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxBoolean
                                                                ref={this.NSBoxBoolean_HTTP_IgnoreSslTls}
                                                                title="Ignore SSL/TLS"
                                                                required={false}
                                                            />
                                                        </>
                                                    }
                                                    {
                                                        this.state.monitor_type === MonitorType.Ping &&
                                                        <>
                                                            <NSBoxString
                                                                ref={this.NSBoxString_Ping_Host}
                                                                required={false}
                                                                title="Host"
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSRow>
                                                                <NSBoxString
                                                                    ref={this.NSBoxString_Ping_MaxPackets}
                                                                    required={false}
                                                                    title="Max Packets"
                                                                />
                                                                <NSBoxString
                                                                    ref={this.NSBoxString_Ping_PacketSize}
                                                                    required={false}
                                                                    title="Packet Size"
                                                                />
                                                            </NSRow>
                                                        </>
                                                    }
                                                    {
                                                        this.state.monitor_type === MonitorType.Port &&
                                                        <NSRow>
                                                            <NSBoxString
                                                                ref={this.NSBoxString_Port_Host}
                                                                required={false}
                                                                title="Host"
                                                            />
                                                            <NSBoxInteger
                                                                ref={this.NSBoxInteger_Port_Port}
                                                                required={false}
                                                                title="Port"
                                                            />
                                                        </NSRow>
                                                    }
                                                    {
                                                        this.state.monitor_type === MonitorType.Database &&
                                                        <>
                                                            <NSBoxEntity<DatabaseRow>
                                                                ref={this.NSBoxEntity_Database_CredentialID}
                                                                title="Credential"
                                                                required={false}
                                                                multiple={false}
                                                                table_name="database"
                                                                table={namirasoft.credential.tables.database}
                                                                getValue={(item) => item.id}
                                                                getTitle={(item) => item.name}
                                                                getSort={() => null}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxTextArea
                                                                ref={this.NSBoxTextArea_Database_Query}
                                                                required={false}
                                                                title="Query"
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                        </>
                                                    }
                                                    {
                                                        this.state.monitor_type === MonitorType.Cache &&
                                                        <NSBoxEntity<DatabaseRow>
                                                            ref={this.NSBoxEntity_Cache_CredentialID}
                                                            title="Credential"
                                                            required={false}
                                                            multiple={false}
                                                            table_name="cache"
                                                            table={namirasoft.infra.tables.cache}
                                                            getValue={(item) => item.id}
                                                            getTitle={(item) => item.name}
                                                            getSort={() => null}
                                                            style={{ width: NSBox.width.double }}
                                                        />
                                                    }
                                                    {
                                                        this.state.monitor_type === MonitorType.Messaging &&
                                                        <NSBoxEntity<DatabaseRow>
                                                            ref={this.NSBoxEntity_Messaging_CredentialID}
                                                            title="Credential"
                                                            required={false}
                                                            multiple={false}
                                                            table_name="messaging"
                                                            table={namirasoft.infra.tables.messaging}
                                                            getValue={(item) => item.id}
                                                            getTitle={(item) => item.name}
                                                            getSort={() => null}
                                                            style={{ width: NSBox.width.double }}
                                                        />
                                                    }
                                                    {
                                                        this.state.monitor_type === MonitorType.DockerContainer &&
                                                        <>
                                                            <NSBoxEnum<MonitorDockerContainerConnectionType>
                                                                ref={this.NSBoxEnum_DockerContainer_ConnectionType}
                                                                title="Connection Type"
                                                                required={false}
                                                                multiple={false}
                                                                getEnumObject={() => MonitorDockerContainerConnectionType}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxString
                                                                ref={this.NSBoxString_DockerContainer_Deamon}
                                                                required={false}
                                                                title="Daemon"
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxString
                                                                ref={this.NSBoxString_DockerContainer_IdOrName}
                                                                required={false}
                                                                title="ID or Name"
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                        </>
                                                    }
                                                    {
                                                        this.state.monitor_type === MonitorType.KubernetesPod &&
                                                        <>
                                                            <NSBoxEnum<MonitorKubernetesPodControllerType>
                                                                ref={this.NSBoxEnum_KubernetesPod_ControllerType}
                                                                title="Controller Type"
                                                                required={false}
                                                                multiple={false}
                                                                getEnumObject={() => MonitorKubernetesPodControllerType}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxString
                                                                ref={this.NSBoxString_KubernetesPod_ControllerName}
                                                                title="Controller Name"
                                                                required={false}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                        </>
                                                    }
                                                </NSColumn>
                                            )
                                        },

                                    ]
                                }

                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        required
                        title="Name"
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<ProjectRow>
                        ref={this.NSBoxEntity_ProjectID}
                        title="Project"
                        required
                        multiple={false}
                        table_name="project"
                        table={namirasoft.infra.tables.project}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_project_id: box.getValueOne() ?? undefined }),
                                () =>
                                {
                                    this.NSBoxEntity_EnvironmentID.current?.setValue(null);
                                    this.NSBoxEntity_EnvironmentID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<EnvironmentRow>
                        ref={this.NSBoxEntity_EnvironmentID}
                        title="Environment"
                        required
                        multiple={false}
                        table_name="environment"
                        list={async (filters, page, size, sorts) =>
                        {
                            if (!filters)
                                filters = [];

                            if (this.state.selected_project_id)
                                filters.push(new FilterItem(
                                    namirasoft.infra.tables.environment,
                                    namirasoft.infra.tables.environment.columns.project_id,
                                    false,
                                    FilterItemOperator.all.equals,
                                    this.state.selected_project_id,
                                ));

                            return await this.props.servers.infra.environment.List(filters, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_environment_id: box.getValueOne(false) ?? "" }),
                                () =>
                                {
                                    this.NSBoxEntity_CallerServerID.current?.setValue(null);
                                    this.NSBoxEntity_CallerKubernetesID.current?.setValue(null);
                                    this.NSBoxEntity_CallerServerID.current?.reload();
                                    this.NSBoxEntity_CallerKubernetesID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<LogGroupRow>
                        ref={this.NSBoxEntity_LogGroupID}
                        title="Log Group"
                        required={false}
                        multiple={false}
                        table_name="log-group"
                        table={namirasoft.log.tables.log_group}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<TopicRow>
                        ref={this.NSBoxEntity_TopicID}
                        title="Topic"
                        required={false}
                        multiple
                        table_name="topic"
                        table={namirasoft.notification_sender.tables.topic}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxBoolean
                        ref={this.NSBoxBoolean_UpSideDown}
                        title="Up Side Down"
                        required
                        hideHeader
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required={false}
                        title="Description"
                        style={{ width: NSBox.width.double }}
                    />
                </NSColumn>
            </InfraLayout >
        );
    }
}